import React, { useEffect, useRef, useState } from 'react'

import { Button, Divider, Form, Input, Radio, Upload, message } from 'antd'
import { UploadOutlined } from '@ant-design/icons'

import './style.scss'
import { attemptToAssestment, editAttempt, getUserById } from '../../../../../Constants/Api/functions/function'
import { UPLOAD_ASSESSMENT_DOCUMENT_URL } from '../../../../../Constants/Api/azure/azure'

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
        const dateObj = new Date(dateStr);
        if (isNaN(dateObj.getTime())) return dateStr;
        return dateObj.toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
        return dateStr;
    }
}

const SubmitAssessment = ({
    assessmentData,
    handleGetAssessment,
    userProfile,
    contextHolder,
    api
}) => {
    const [attempts, setAttempts] = useState()
    const formRef = useRef();
    const [isLoading, setIsLoading] = useState(false);
    const [name, setName] = useState();
    const [submissionType, setSubmissionType] = useState('url');
    const [uploadedFile, setUploadedFile] = useState(null);

    useEffect(() => {
        if (assessmentData) {
            if (!assessmentData?.attempt?.id) {
                setAttempts(1) // 1 initial submission attempt remaining
            } else {
                setAttempts(2 - assessmentData?.attempt?.attemptNumber) // remaining edit attempts (max 1 edit chance)
            }

            if (assessmentData?.attempt?.isReviewed) {
                renderName(assessmentData?.attempt?.reviewdBy).then(res => {
                    setName(res);
                })
            }
        }
    }, [assessmentData])

    const uploadFileAndGetUrl = async () => {
        if (!uploadedFile) {
            message.error('Please select a document to upload.');
            return null;
        }
        try {
            const formData = new FormData();
            formData.append('file', uploadedFile);
            formData.append('fileName', `assessment_${assessmentData?.id || 'submission'}_${userProfile?.id || 'user'}_${Date.now()}`);

            const uploadResponse = await fetch(UPLOAD_ASSESSMENT_DOCUMENT_URL, {
                method: 'POST',
                body: formData,
            });
            const uploadData = await uploadResponse.json();

            if (!uploadData.success) {
                throw new Error(uploadData.message || 'Failed to upload document');
            }
            return uploadData.url;
        } catch (error) {
            console.error('uploadFileAndGetUrl', error);
            message.error('File upload failed: ' + error.message);
            return null;
        }
    }

    const onFinish = async (values) => {
        setIsLoading(true);
        let submissionUrl = values.submissionUrl;

        if (submissionType === 'document') {
            const uploadedUrl = await uploadFileAndGetUrl();
            if (!uploadedUrl) {
                setIsLoading(false);
                return;
            }
            submissionUrl = uploadedUrl;
        }

        const submitData = {
            userId: userProfile?.id,
            assessmentId: assessmentData?.id,
            submissionUrl: submissionUrl
        };

        try {
            const response = await attemptToAssestment(submitData);
            if (response && response.status === 200) {
                openNotification('success', 'Assessment submitted successfully', '')
                formRef.current.resetFields();
                setUploadedFile(null);
                setTimeout(() => {
                    handleGetAssessment();
                }, 1000);
            } else {
                openNotification('error', 'Failed to submit assessment', '')
            }
        } catch (error) {
            console.log(error);
            openNotification('error', 'Failed to submit assessment', '')
        }
        setTimeout(() => {
            setIsLoading(false);
        }, 1000);
    }
    const openNotification = (type, message, description) => {
        api[type]({
            message: message,
            description: description,
        });
    }

    const onFinishReSubmit = async (values) => {
        setIsLoading(true);
        let submissionUrl = values.submissionUrl;

        if (submissionType === 'document') {
            const uploadedUrl = await uploadFileAndGetUrl();
            if (!uploadedUrl) {
                setIsLoading(false);
                return;
            }
            submissionUrl = uploadedUrl;
        }

        const submitData = {
            id: assessmentData?.attempt?.id,
            userId: userProfile?.id,
            assessmentId: assessmentData?.id,
            submissionUrl: submissionUrl,
            attemptNumber: 2 // mark as second attempt / edit chance consumed
        };

        try {
            const response = await editAttempt(submitData);
            if (response && response.status === 200) {
                openNotification('success', 'Assessment re-submitted successfully', '')
                formRef.current.resetFields();
                setUploadedFile(null);
                setTimeout(() => {
                    handleGetAssessment();
                }, 1000);
            } else {
                openNotification('error', 'Failed to re-submit assessment', '')
            }
        }
        catch (error) {
            console.log(error);
            openNotification('error', 'Failed to re-submit assessment', '')
        }
        setTimeout(() => {
            setIsLoading(false);
        }, 1000);
    }

    const renderName = async (id) => {
        try {
            const res = await getUserById(id);
            return res?.name;
        } catch (error) {
            console.log(error);
            return id;
        }
    }

    const isExpired = assessmentData?.endDate ? new Date(assessmentData.endDate) < new Date() : false;

    return (
        <div className='submit-assessment'>
            {contextHolder}
            <h2>Submit Assessment</h2>

            <p>{!assessmentData?.attempt ?
                <span>
                    You have not submitted this assessment yet. Please submit your assessment
                    {` before the due date: ${assessmentData?.endDate ? formatDate(assessmentData.endDate) : ''}`}
                </span>
                :
                <>
                    You have already attempted this assessment.
                </>
            }
            </p>

            {!assessmentData?.attempt?.id ? (
                <h3 className="attempts-status remaining">
                    You have <strong>1 submission attempt</strong> and <strong>1 editing chance</strong> remaining.
                </h3>
            ) : assessmentData?.attempt?.attemptNumber === 1 ? (
                <h3 className="attempts-status edit-chance">
                    You have used your submission attempt. You have <strong>1 editing chance</strong> remaining.
                </h3>
            ) : (
                <h3 className="attempts-status no-chances">
                    You have <strong>no remaining attempts or editing chances</strong>.
                </h3>
            )}

            <Divider />

            {isExpired && (
                <div className="deadline-notice expired">
                    ⚠️ Submission Closed: The deadline has passed. No further submissions or edits are allowed.
                </div>
            )}

            {assessmentData?.attempt?.id && <div className='attempt-data'>
                <h4>
                    Your submission details are as follows:
                </h4>
                <h3>
                    <span className='lable'>Attempted Date & Time: </span>
                    <span className='value'>{assessmentData?.attempt?.createdAt ? formatDate(assessmentData?.attempt?.createdAt) : ''}</span>
                </h3>
                <h3>
                    <span className='lable'>
                        Submission URL:
                    </span>
                    <span className='value'>
                        <a
                            href={assessmentData?.attempt?.submissionUrl}
                            target='_blank'
                            rel='noreferrer'
                            className='value'>
                            {assessmentData?.attempt?.submissionUrl}
                        </a>
                    </span>
                </h3>
                <h3>
                    <span className='lable'>
                        Is Reviewed:
                    </span>
                    <span className='value'>
                        {assessmentData?.attempt?.isReviewed ? 'Yes' : 'No'}
                    </span>
                </h3>
                {assessmentData?.attempt?.isReviewed &&
                    <>
                        <h3>
                            <span className='lable'>
                                Reviewed Date:
                            </span>
                            <span className='value'>
                                {assessmentData?.attempt?.updatedAt ? formatDate(assessmentData?.attempt?.updatedAt) : ''}
                            </span>
                        </h3>
                        <h3>
                            <span className='lable'>
                                Reviewed By:
                            </span>
                            <span className='value'>
                                {name}
                            </span>
                        </h3>
                        <h3>
                            <span className='lable'>
                                Marks:
                            </span>
                            <span className='value'>
                                {assessmentData?.attempt?.marks}
                            </span>
                        </h3>
                        {assessmentData?.attempt?.comments && (
                            <h3>
                                <span className='lable'>
                                    Comments:
                                </span>
                                <span className='value'>
                                    {assessmentData?.attempt?.comments}
                                </span>
                            </h3>
                        )}
                    </>
                }
                {(attempts > 0 && !assessmentData?.attempt?.isReviewed && !isExpired) &&
                    <>
                        <Divider />
                        <div className='re-submit'>
                            <h4>
                                If you want to re-submit the assessment, please select the submission method below:
                            </h4>
                            <p className='instruct'>
                                Select "Submit Link" for quiz or test submissions (by entering the email address used) or for project/assignment links. Select "Upload Document" if you prefer to upload a file directly.
                            </p>
                            <Form
                                layout="vertical"
                                onFinish={onFinishReSubmit}
                                ref={formRef}
                            >
                                <Form.Item label="Submission Type">
                                    <Radio.Group onChange={(e) => {
                                        setSubmissionType(e.target.value);
                                        setUploadedFile(null);
                                    }} value={submissionType}>
                                        <Radio value="url">Submit Link / URL</Radio>
                                        <Radio value="document">Upload Document</Radio>
                                    </Radio.Group>
                                </Form.Item>

                                {submissionType === 'url' ? (
                                    <Form.Item
                                        label='Submission URL'
                                        name="submissionUrl"
                                        rules={[{
                                            required: true,
                                            message: 'Please enter submission URL!'
                                        }]}>
                                        <Input
                                            placeholder='Please enter submission URL'
                                        />
                                    </Form.Item>
                                ) : (
                                    <Form.Item
                                        label='Upload Document'
                                        required={true}
                                    >
                                        <Upload
                                            beforeUpload={(file) => {
                                                setUploadedFile(file);
                                                return false;
                                            }}
                                            maxCount={1}
                                            onRemove={() => setUploadedFile(null)}
                                            fileList={uploadedFile ? [uploadedFile] : []}
                                        >
                                            <Button icon={<UploadOutlined />}>Select Document File</Button>
                                        </Upload>
                                        <div style={{ marginTop: '8px', color: '#8c8c8c', fontSize: '12px' }}>
                                            Supported formats: PDF, DOC/DOCX, ZIP/RAR, TXT, PPT/PPTX, XLS/XLSX, or images. Max size: 50MB.
                                        </div>
                                    </Form.Item>
                                )}

                                <Button
                                    loading={isLoading}
                                    type="primary" htmlType="submit">
                                    Re-submit Assessment
                                </Button>

                            </Form>
                        </div>
                    </>}
            </div>
            }

            {!assessmentData?.attempt?.id && !isExpired &&
                <>
                    <h4>
                        Please Read the Instructions Carefully
                    </h4>
                    <p className='instruct'>
                        Select "Submit Link" for quiz or test submissions (by entering the email address used) or for project/assignment links. Select "Upload Document" if you prefer to upload a file directly.
                    </p>
                    <Form
                        layout="vertical"
                        onFinish={onFinish}
                        ref={formRef}
                    >
                        <Form.Item label="Submission Type">
                            <Radio.Group onChange={(e) => {
                                setSubmissionType(e.target.value);
                                setUploadedFile(null);
                            }} value={submissionType}>
                                <Radio value="url">Submit Link / URL</Radio>
                                <Radio value="document">Upload Document</Radio>
                            </Radio.Group>
                        </Form.Item>

                        {submissionType === 'url' ? (
                            <Form.Item
                                label='Submission URL'
                                name="submissionUrl"
                                rules={[{
                                    required: true,
                                    message: 'Please enter submission URL!'
                                }]}>
                                <Input
                                    placeholder='Please enter submission URL'
                                />
                            </Form.Item>
                        ) : (
                            <Form.Item
                                label='Upload Document'
                                required={true}
                            >
                                <Upload
                                    beforeUpload={(file) => {
                                        setUploadedFile(file);
                                        return false;
                                    }}
                                    maxCount={1}
                                    onRemove={() => setUploadedFile(null)}
                                    fileList={uploadedFile ? [uploadedFile] : []}
                                >
                                    <Button icon={<UploadOutlined />}>Select Document File</Button>
                                </Upload>
                                <div style={{ marginTop: '8px', color: '#8c8c8c', fontSize: '12px' }}>
                                    Supported formats: PDF, DOC/DOCX, ZIP/RAR, TXT, PPT/PPTX, XLS/XLSX, or images. Max size: 50MB.
                                </div>
                            </Form.Item>
                        )}

                        <Button
                            loading={isLoading}
                            type="primary" htmlType="submit">
                            Submit Assessment
                        </Button>

                    </Form>
                </>}
        </div>
    )
}

export default SubmitAssessment